import React, { useEffect, useState } from "react";
import {
  Grid,
  Typography,
  Box,
  InputAdornment,
  Card,
  CardContent,
  CircularProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import {
  fetchVendorDetailsRequest,
  resetVendorState,
  updateVendorApprovalStatusRequest
} from "../../redux/Vendors/actions/vendorActions";
import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import "../../styles/vendorStyle.css";
import TextInputField from "../../component/ui/FormFields/TextInputField";
import CustomButton from "../../component/ui/FormFields/Button";
import { VENDORS } from "../../component/common/Routes/AppRoutes";
import { useForm } from "react-hook-form";
import Loading from "../../component/ui/Loading";
import { toast, ToastContainer } from "react-toastify";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import StatusRadioGroup from "../../component/ui/FormFields/StatusRadioGroup";
import DocumentViewer from "./DocumentViewer";

const ViewVendor = () => {
  const { vendorId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const vendorDetailsState = useSelector(
    (state) => state.vendors.vendorDetails
  );

  const user = useSelector((state) => state.auth.user);
  const [approvalStatus, setApprovalStatus] = useState("");
  const { control } = useForm();
  const vendorDetails = vendorDetailsState?.data || {};
  const viewLoading = vendorDetailsState?.loading || false;
  const approvalSuccess = vendorDetailsState?.approval_message;
  const [initialStatus, setInitialStatus] = useState("");

  useEffect(() => {
    if (vendorId) {
      dispatch(fetchVendorDetailsRequest(vendorId));
    }
  }, [dispatch, vendorId]);
  useEffect(() => {
    if (approvalSuccess) {
      toast.success("Approval Status Changed", {
        position: "top-center",
        autoClose: 3000,
        onClose: () => {
          resetVendorState();
          navigate(VENDORS);
        }
      });
    }
  }, [approvalSuccess]);
  useEffect(() => {
    if (vendorDetails?.approove_status) {
      setApprovalStatus(vendorDetails?.approove_status);
      setInitialStatus(vendorDetails?.approove_status);
    }
  }, [vendorDetails]);

  const {
    agency_name,
    first_name,
    last_name,
    email,
    phone,
    address1,
    address2,
    city_name,
    country_code,
    state_name,
    country_name,
    pincode,
    vendor_serial_no,
    pan_number,
    gstin_number,
    commission_rate,
    iata_reg_no,
    company_reg_number,
    proof_image,
    discount
  } = vendorDetails || {};

  const baseVendorFields = [
    {
      label: "Vendor Serial Number",
      value: vendor_serial_no || "-",
      icon: <PersonIcon />,
      xs: 12,
      sm: 6
    },
    {
      label: "Agency / Company Name",
      value: agency_name || "-",
      icon: <BusinessIcon />,
      xs: 12,
      sm: 6
    },
    {
      label: "First Name",
      value: first_name || "-",
      icon: <PersonIcon />,
      xs: 12,
      sm: 6
    },
    {
      label: "Last Name",
      value: last_name || "-",
      icon: <PersonIcon />,
      xs: 12,
      sm: 6
    },
    {
      label: "Email",
      value: email || "-",
      icon: <EmailIcon />,
      xs: 12,
      sm: 6
    },
    {
      name: "country_code",
      value: country_code || "-",
      label: "code",
      sm: 2,
      xs: 4
    },
    {
      label: "Phone",
      value: phone || "-",
      icon: <PhoneIcon />,
      xs: 8,
      sm: 4
    },
    {
      label: "Address1",
      value: address1 || "-",
      icon: <LocationOnIcon />,
      xs: 12,
      sm: 6
    },
    {
      label: "Address2",
      value: address2 || "-",
      icon: <LocationOnIcon />,
      xs: 12,
      sm: 6
    },
    {
      label: "Country",
      value: country_name || "-",
      icon: <LocationOnIcon />,
      xs: 12,
      sm: 6
    },

    {
      label: "State",
      value: state_name || "-",
      icon: <LocationOnIcon />,
      xs: 12,
      sm: 6
    },

    {
      label: "City",
      value: city_name || "-",
      icon: <LocationOnIcon />,
      xs: 12,
      sm: 6
    },
    {
      label: "Pincode",
      value: pincode || "-",
      icon: <LocationOnIcon />,
      xs: 12,
      sm: 6
    },
    {
      label: "Vendor Commission",
      value: discount || "-",
      icon: <PersonIcon />,
      xs: 12,
      sm: 6
    },
    {
      label: "IATA Reg Number",
      value: iata_reg_no || "-",
      icon: <PersonIcon />,
      xs: 12,
      sm: 6
    }
  ];

  const isIndiaVendor = country_name?.toLowerCase() === "india";
  const vendorFields = [
    ...baseVendorFields,
    ...(isIndiaVendor
      ? [
          {
            label: "PAN Number",
            value: pan_number || "-",
            icon: <PersonIcon />,
            xs: 12,
            sm: 6
          },
          {
            label: "GSTIN Number",
            value: gstin_number || "-",
            icon: <PersonIcon />,
            xs: 12,
            sm: 6
          }
        ]
      : [
          {
            label: "Company Registration Number",
            value: company_reg_number || "-",
            icon: <PersonIcon />,
            xs: 12,
            sm: 6
          }
        ])
  ];

  const vendorStatusMap = {
    0: "Active",
    1: "Inactive"
  };

  const vendorStatus = vendorStatusMap[vendorDetails.status] || "Unknown";

  const handleApprovalStatusChange = (e) => {
    setApprovalStatus(e.target.value);
  };

  const handleApprovalStatusSubmit = async () => {
    const payload = {
      vendor_id: vendorDetails.id,
      status: approvalStatus,
      discount: "15%"
    };

    try {
      await dispatch(updateVendorApprovalStatusRequest(payload));
      await dispatch(fetchVendorDetailsRequest(vendorId));
    } catch (error) {
      console.error("Error updating vendor approval status:", error);
    }
  };
  const showPendingOption = initialStatus === "2" || initialStatus === 2;
  if (viewLoading) {
    return <Loading text="Loading Vendor details..." />;
  }
  return (
    <>
      <ToastContainer />
      <Box className="container">
        <Card className="view-vendor-card">
          <CardContent>
            <Typography variant="h5" className="form-heading">
              View Vendor
            </Typography>
            <Grid
              container
              rowSpacing={4}
              columnSpacing={{ xs: 1, sm: 1, md: 1, lg: 3 }}
              className="vendor-form-margin"
            >
              {vendorFields.map((field, index) => (
                <Grid item xs={field.xs} sm={field.sm} key={index}>
                  <TextInputField
                    label={field.label}
                    value={field.value || "N/A"}
                    disabled={true}
                    startAdornment={
                      <InputAdornment position="start">
                        {field.icon}
                      </InputAdornment>
                    }
                  />
                </Grid>
              ))}

              {vendorDetails?.proof_image && (
                <DocumentViewer fileUrl={vendorDetails?.proof_image} />
              )}

              <Grid item xs={12} sm={6}>
                <StatusRadioGroup
                  name={"status"}
                  control={control}
                  defaultValue={vendorStatus}
                  disabled={true}
                />
              </Grid>

              {user?.role === "Admin" && (
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    Approval Status
                  </Typography>
                  <Grid
                    container
                    spacing={2}
                    direction="row"
                    alignItems="center"
                  >
                    <Grid item>
                      <RadioGroup
                        value={approvalStatus}
                        onChange={handleApprovalStatusChange}
                        row
                      >
                        <FormControlLabel
                          value={1}
                          control={<Radio color="success" />}
                          label="Approved"
                        />
                        {showPendingOption && (
                          <FormControlLabel
                            value={2}
                            control={<Radio color="warning" />}
                            label="Pending"
                          />
                        )}
                        <FormControlLabel
                          value={3}
                          control={<Radio color="error" />}
                          label="Rejected"
                        />
                      </RadioGroup>
                    </Grid>
                    <Grid item>
                      <CustomButton
                        variant="contained"
                        className="success-button"
                        text="Submit Approval Status"
                        onClick={handleApprovalStatusSubmit}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </Grid>

            <Box className="button-container">
              <CustomButton
                variant="contained"
                className="cancel-button"
                text="Back"
                onClick={() => navigate(-1)}
              />

              <CustomButton
                variant="contained"
                className="success-button"
                text="Edit"
                onClick={() => navigate(`/vendors/edit/${vendorDetails.id}`)}
              />
            </Box>
          </CardContent>
        </Card>
      </Box>
    </>
  );
};

export default ViewVendor;
